// Mirrors pi-poc/src/protocol.ts (Cadence spec #132); keep the two in sync

export const MAX_TEXT_CHARS = 20_000;

export type ThinkingLevel =
  | 'off'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max';
export interface ModelRef {
  provider: string;
  id: string;
}
export interface ModelInfo extends ModelRef {
  name: string;
  thinkingLevels: ThinkingLevel[];
}
export interface SessionContext {
  targetType?:
    | 'dashboard'
    | 'homepage'
    | 'chart_group'
    | 'workflow_step'
    | 'business_app';
  dashboardId?: string;
  dashletId?: string;
  objectId?: string;
  automationId?: string;
  stepId?: string;
  appApiName?: string;
}
export interface UserLocation {
  path: string;
}
export interface Usage {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  totalTokens: number;
  costTotal: number;
}
export interface SourceFile {
  path: string;
  content: string;
}
export interface SnapshotRef {
  id: string;
  createdAt: string; // ISO 8601
  reason: 'push' | 'code_step' | 'workspace';
  dashletId: string | null; // null for code_step versions
  filesUrl?: string; // presigned GET of the Snapshot JSON; absolute or relative to the ws URL's http(s) origin
}
export interface Snapshot extends Omit<SnapshotRef, 'filesUrl'> {
  files: SourceFile[];
  blobs?: { path: string; sha256: string; size: number }[];
}
export interface StoredCapture {
  path: string;
  width: number;
  height: number;
}
export type HistoryItem =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string }
  | {
      kind: 'tool';
      toolName: string;
      isError: boolean;
      summary: string;
      toolCallId?: string;
      capture?: StoredCapture;
      label?: string;
    };
export interface PushInfo {
  action: 'created' | 'updated';
  dashboardId: string;
  dashletId: string;
  url: string;
}
export interface SessionCredentials {
  apiKey: string;
  userId: string;
  businessId: string;
  expires: string | number;
}
export const INTERACTIVE_CAPABILITY = 'interactive';
export const CAPTURE_CAPABILITY = 'capture';
export const NAVIGATE_CAPABILITY = 'navigate';
export const REFRESH_PLUGINS_CAPABILITY = 'refresh_plugins';
export type ClientCapability =
  | typeof CAPTURE_CAPABILITY
  | typeof INTERACTIVE_CAPABILITY
  | typeof NAVIGATE_CAPABILITY
  | typeof REFRESH_PLUGINS_CAPABILITY;
export type CaptureFailureCode =
  | 'not_rendered'
  | 'minimized'
  | 'timeout'
  | 'capture_failed'
  | 'unsupported'
  | 'too_large';
export interface CaptureUploadTarget {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  maxBytes: number;
}
export interface CaptureUpload {
  png: CaptureUploadTarget;
  failure: CaptureUploadTarget;
  expiresAt: number; // unix ms
}
export type UiCaptureTarget =
  | { kind: 'floating_frame'; frameApiName: string }
  | { kind: 'page' };
export interface CaptureFailureBody {
  code: CaptureFailureCode;
  message?: string;
}
export interface ActionCodeStepSecret {
  id?: string;
  name: string;
}
/** An input's source, set by `input_type`: only that one key is present. */
export interface ActionCodeStepInput {
  input_type: 'variable' | 'field' | 'related_field' | 'static_value';
  name: string;
  data_type: string;
  is_list: boolean;
  variable?: { name: string };
  field?: { id: string };
  related_field?: { id: string };
  /** `{ value }`; employee: `{ employee_id }`; entity: `{ custom_object: { id }, entity_record_id }`. */
  static_value?: {
    value?: unknown;
    employee_id?: string | null;
    custom_object?: { id: string };
    entity_record_id?: string | null;
  };
}
export interface ActionCodeStepOutput {
  output_type: 'variable' | 'field' | 'related_field';
  name: string;
  data_type: string;
  is_list: boolean;
  conflict_resolution: string;
  create_missing_option: boolean;
  variable?: { name: string };
  field?: { id: string };
  related_field?: { id: string };
}
export interface ActionCodeStepWrite {
  runtime: string;
  script: string;
  secrets: ActionCodeStepSecret[];
  inputs: ActionCodeStepInput[];
  outputs: ActionCodeStepOutput[];
}
export interface ResultUpload {
  result: CaptureUploadTarget;
  expiresAt: number; // unix ms
}
export type CodeStepSaveFailureCode =
  | 'not_open'
  | 'step_missing'
  | 'dirty'
  | 'conflict'
  | 'rejected'
  | 'forbidden'
  | 'unsupported'
  | 'failed';
export type CodeStepSaveResult =
  | { ok: true; revision?: number }
  | {
      ok: false;
      code: CodeStepSaveFailureCode;
      message?: string;
      errors?: string[];
    };
export const MAX_CODE_STEP_SAVE_RESULT_BYTES = 16 * 1024;
export const MAX_CODE_STEP_SAVE_MESSAGE_CHARS = 300;
export const MAX_CODE_STEP_DESCRIPTION_CHARS = 200;
export const MAX_CODE_STEP_SAVE_ERRORS = 20;
export type NavigateFailureCode = 'declined' | 'busy' | 'unavailable';
export type NavigateResult =
  | { ok: true; path: string }
  | { ok: false; code: NavigateFailureCode; message?: string };
export type RefreshPluginsResult =
  | { ok: true }
  | { ok: false; code: 'unavailable'; message?: string };
export type OpenFloatingFrameResult =
  | { ok: true }
  | { ok: false; code: 'not_found' | 'hidden' | 'unavailable' };
const MAX_LOCATION_PATH_CHARS = 1024;
const MAX_API_NAME_CHARS = 100;
const API_NAME_RE = new RegExp(
  `^[A-Za-z0-9][A-Za-z0-9_]{0,${MAX_API_NAME_CHARS - 1}}$`
);
export const isApiName = (v: unknown): v is string =>
  typeof v === 'string' && API_NAME_RE.test(v);
const UNSAFE_CHARS_RE = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;
export const isLocationPath = (v: unknown): v is string =>
  typeof v === 'string' &&
  v.startsWith('/') &&
  v.length <= MAX_LOCATION_PATH_CHARS &&
  !UNSAFE_CHARS_RE.test(v);
export const isNavigatePath = (v: unknown): v is string =>
  isLocationPath(v) && !v.startsWith('//') && !v.includes('\\');
export type ClientMessage =
  | ({
      type: 'session.start';
      context: SessionContext;
      model?: ModelRef;
      thinkingLevel?: ThinkingLevel;
      capabilities?: ClientCapability[];
      credentials: SessionCredentials;
      profile?: string;
    } & (
      | { conversationId?: string; newConversation?: never }
      | { conversationId?: never; newConversation?: true }
    ))
  | {
      type: 'snapshot.restore';
      snapshotId: string;
      credentials?: SessionCredentials;
    }
  | {
      type: 'user.message';
      text: string;
      credentials: SessionCredentials;
      clientMessageId: string;
      location?: UserLocation;
    }
  | { type: 'session.abort' };
export type ServerMessage =
  | {
      type: 'session.ready';
      sessionId: string;
      models: ModelInfo[];
      model: ModelRef;
      thinkingLevel: ThinkingLevel;
      context: SessionContext;
      limits: { maxMs: number; idleMs: number };
      conversationId: string;
      resumed: boolean;
      turnActive: boolean;
      history: HistoryItem[];
      snapshots?: SnapshotRef[];
      snapshotPointer?: number;
    }
  | { type: 'assistant.delta'; kind: 'text' | 'thinking'; delta: string }
  | {
      type: 'assistant.message';
      text: string;
      usage?: Usage;
      stopReason: string;
    }
  | {
      type: 'tool.generating';
      toolCallId: string;
      toolName: string;
      argsSummary?: string;
      chars: number;
      label?: string;
    }
  | {
      type: 'tool.start';
      toolCallId: string;
      toolName: string;
      args: unknown;
      label?: string;
    }
  | { type: 'tool.update'; toolCallId: string; partial: string }
  | {
      type: 'tool.end';
      toolCallId: string;
      toolName: string;
      isError: boolean;
      summary: string;
      capture?: StoredCapture;
    }
  | { type: 'turn.settled' }
  | {
      type: 'push.result';
      action: 'created' | 'updated';
      dashboardId: string;
      dashletId: string;
      url: string;
      conversationId: string;
      snapshot: SnapshotRef;
    }
  | {
      type: 'snapshot.restored';
      snapshotId: string;
      /** Block restores only (always present for reason 'push'). */
      push?: PushInfo;
      filesUrl: string;
    }
  | ErrorFrame
  | { type: 'message.accepted'; clientMessageId: string }
  | {
      type: 'block.capture';
      requestId: string;
      dashletId: string;
      toolCallId?: string;
      upload: CaptureUpload;
    }
  | { type: 'version.added'; snapshot: SnapshotRef }
  | {
      type: 'code_step.save';
      requestId: string;
      toolCallId?: string;
      automationId: string;
      stepId: string;
      description?: string;
      actionCodeStep: ActionCodeStepWrite;
      upload: ResultUpload;
    }
  | {
      type: 'ui.navigate';
      requestId: string;
      toolCallId: string;
      path: string;
      upload: ResultUpload;
    }
  | {
      type: 'ui.refresh_plugins';
      requestId: string;
      toolCallId: string;
      upload: ResultUpload;
    }
  | {
      type: 'ui.open_floating_frame';
      requestId: string;
      toolCallId: string;
      frameApiName: string;
      upload: ResultUpload;
    }
  | {
      type: 'ui.capture';
      requestId: string;
      toolCallId: string;
      target: UiCaptureTarget;
      fullPage?: true;
      upload: CaptureUpload;
    }
  | { type: 'business_app.created'; appApiName: string }
  | { type: 'session.closed'; reason: SessionClosedReason };

export const SESSION_CLOSED_REASONS = [
  'client',
  'timeout_max',
  'timeout_idle',
  'auth',
  'server_error',
  'capacity',
  'conflict',
  'not_found',
  'forbidden',
] as const;
export type SessionClosedReason = (typeof SESSION_CLOSED_REASONS)[number];

export const ERROR_CODES = [
  'bad_message',
  'not_ready',
  'already_started',
  'invalid_context',
  'bad_request',
  'auth',
  'not_found',
  /**
   * conversationId may not be continued by this caller, or the caller lacks the edit/push right on
   * its resource (checked at each turn start). Closes 'forbidden'. Echoes on a user.message.
   */
  'forbidden',
  'conflict',
  'session_create_failed',
  'conversation_invalid',
  /**
   * Model unavailable. At session start: closes 'server_error'. On the webapp backend a message
   * whose lease did not vend the session's model is refused with it (echoes; the socket stays open).
   */
  'model_unavailable',
  'busy',
  'restore_busy',
  'state_unavailable',
  'closed',
  'cancelled',
  'persist_failed',
  'files_skipped',
  'prompt_failed',
  'abort_failed',
  'unsupported',
  'unknown_snapshot',
  'restore_failed',
  'restore_note_failed',
  'kizen_ai_unavailable',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const AUTH_REASONS = [
  'missing',
  'expired',
  'rejected',
  'unverified',
] as const;
export const BAD_REQUEST_REASONS = [
  'unknown_profile',
  'missing_required_pack',
  'invalid_conversation_id',
  'context_rejected',
  'profile_mismatch',
  'binding_mismatch',
  'new_conversation_with_id',
] as const;
export const BUSY_REASONS = [
  /** Another tab or a runTurn caller holds the conversation. */
  'conversation_locked',
  'save_pending',
  /** Another session holds the resource (code step, block, app) this conversation edits. */
  'resource_locked',
] as const;
export const STATE_UNAVAILABLE_REASONS = [
  'lock_unavailable',
  'save_pending',
  'head_check_failed',
  'lease_lost',
] as const;
export const KIZEN_AI_UNAVAILABLE_REASONS = [
  'permission',
  'feature_disabled',
  'billing',
] as const;
export const PERSIST_FAILURES = ['turn', 'version', 'timeout'] as const;
export const RESTORE_FAILURES = [
  'no_dashlet',
  'workspace',
  'push',
  'internal',
  'target_changed',
  'target_busy',
  'unknown_outcome',
  'unrestorable',
  'apply',
] as const;
export const SKIPPED_FILE_REASONS = [
  'too_large',
  'not_utf8',
  'too_many',
  'secret',
] as const;
export const CONTEXT_ID_FIELDS = [
  'dashboardId',
  'dashletId',
  'objectId',
  'automationId',
  'stepId',
  'appApiName',
] as const;

export type AuthReason = (typeof AUTH_REASONS)[number];
export type BadRequestReason = (typeof BAD_REQUEST_REASONS)[number];
export type BusyReason = (typeof BUSY_REASONS)[number];
export type StateUnavailableReason = (typeof STATE_UNAVAILABLE_REASONS)[number];
export type KizenAiUnavailableReason =
  (typeof KIZEN_AI_UNAVAILABLE_REASONS)[number];
export type PersistFailure = (typeof PERSIST_FAILURES)[number];
export type RestoreFailure = (typeof RESTORE_FAILURES)[number];
export type SkippedFileReason = (typeof SKIPPED_FILE_REASONS)[number];
export type ContextIdField = (typeof CONTEXT_ID_FIELDS)[number];

export interface ErrorParams {
  auth: { reason: AuthReason };
  bad_request: { reason: BadRequestReason };
  invalid_context: { fields: ContextIdField[] };
  busy: { reason: BusyReason };
  state_unavailable: { reason: StateUnavailableReason };
  persist_failed: { what: PersistFailure };
  files_skipped: {
    files: { path: string; reason: SkippedFileReason }[];
    more: number;
  };
  restore_failed: { reason: RestoreFailure };
  kizen_ai_unavailable: { reason: KizenAiUnavailableReason };
}
export type ErrorCodeWithParams = keyof ErrorParams;
type ErrorFrameOf<C extends ErrorCode> = {
  type: 'error';
  code: C;
  clientMessageId?: string;
} & (C extends ErrorCodeWithParams
  ? { params: ErrorParams[C] }
  : { params?: never });
export type ErrorFrame = { [C in ErrorCode]: ErrorFrameOf<C> }[ErrorCode];
